import type { Geometry } from 'geojson';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';

export enum AudienceMode {
  PEOPLE = 'people',
  AREA = 'area',
  ROLE = 'role',
}

/**
 * One member who holds a role at a particular scope, as
 * `roleScopeMembersForGroup` returns it.
 */
export type RoleScopeMember = {
  userGuid?: string | null;
  displayName?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  scopeId?: string | null;
  scopeType?: string | null;
  scopeLabel?: string | null;
};

export type RoleScopeSelection = {
  roleId: string;
  scopeId: string;
};

export function roleScopeKey(roleId: string, scopeId: string): string {
  return `${roleId}|${scopeId}`;
}

export function parseRoleScopeKey(value: string): RoleScopeSelection | null {
  const separator = value.indexOf('|');
  if (separator <= 0 || separator === value.length - 1) return null;
  return {
    roleId: value.slice(0, separator),
    scopeId: value.slice(separator + 1),
  };
}

export function parseRoleScopeKeys(
  keys: readonly string[]
): RoleScopeSelection[] {
  return keys.flatMap((key) => {
    const parsed = parseRoleScopeKey(key);
    return parsed ? [parsed] : [];
  });
}

/** Distinct role-and-scope choices, labeled so the scope is part of the name. */
export function roleScopeOptions(
  members: readonly RoleScopeMember[]
): { label: string; value: string }[] {
  const seen = new Map<string, { label: string; value: string }>();
  members.forEach((member) => {
    if (
      !member.roleId ||
      !member.scopeId ||
      !member.roleName ||
      !member.scopeLabel
    ) {
      return;
    }
    const value = roleScopeKey(member.roleId, member.scopeId);
    if (seen.has(value)) return;
    seen.set(value, {
      value,
      label: `${member.roleName} · ${member.scopeLabel}`,
    });
  });
  return [...seen.values()];
}

export type RoleScopeGroup = {
  key: string;
  roleId: string;
  scopeId: string;
  roleName: string;
  scopeLabel: string;
  scopeType: string | null;
  members: { userGuid: string; displayName: string }[];
};

/** Each role-and-scope combination with the distinct people who hold it. */
export function roleScopeGroups(
  rows: readonly RoleScopeMember[]
): RoleScopeGroup[] {
  const groups = new Map<
    string,
    Omit<RoleScopeGroup, 'members'> & {
      members: Map<string, { userGuid: string; displayName: string }>;
    }
  >();
  rows.forEach((row) => {
    if (
      !row.userGuid ||
      !row.roleId ||
      !row.scopeId ||
      !row.roleName ||
      !row.scopeLabel
    ) {
      return;
    }
    const key = roleScopeKey(row.roleId, row.scopeId);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        roleId: row.roleId,
        scopeId: row.scopeId,
        roleName: row.roleName,
        scopeLabel: row.scopeLabel,
        scopeType: row.scopeType ?? null,
        members: new Map(),
      };
      groups.set(key, group);
    }
    if (!group.members.has(row.userGuid)) {
      group.members.set(row.userGuid, {
        userGuid: row.userGuid,
        displayName: row.displayName?.trim() || 'Unnamed member',
      });
    }
  });
  const byName = (left: string, right: string) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' });
  return [...groups.values()]
    .map((group) => ({
      ...group,
      members: [...group.members.values()].sort((left, right) =>
        byName(left.displayName, right.displayName)
      ),
    }))
    .sort(
      (left, right) =>
        byName(left.roleName, right.roleName) ||
        byName(left.scopeLabel, right.scopeLabel)
    );
}

/**
 * Users who hold a selected role at that same scope.
 *
 * The same role at another organization, area, or incident is a different
 * audience.
 */
export function userIdsForSelectedRoles(
  assignments: readonly RoleScopeMember[],
  selections: readonly RoleScopeSelection[]
): string[] {
  const wanted = new Set(
    selections.map((selection) =>
      roleScopeKey(selection.roleId, selection.scopeId)
    )
  );
  const users = new Set<string>();
  assignments.forEach((row) => {
    if (!row.userGuid || !row.roleId || !row.scopeId) return;
    if (!wanted.has(roleScopeKey(row.roleId, row.scopeId))) return;
    users.add(row.userGuid);
  });
  return [...users];
}

/** Named people and role members, without sending anyone twice. */
export function unionRecipientIds(
  namedUserIds: readonly string[],
  roleUserIds: readonly string[]
): string[] {
  return [...new Set([...namedUserIds, ...roleUserIds])];
}

/** People who are both selected and inside the area filter. */
export function filterRecipientIds(
  userIds: readonly string[],
  matchUserIds: readonly string[]
): string[] {
  const match = new Set(matchUserIds);
  return userIds.filter((id) => match.has(id));
}

export type AudienceLocationMatch = 'home_aor' | 'current_location';

export type AreaAudience = {
  geometry: Geometry | null;
  aorItemIds: string[];
};

export const EMPTY_AREA_AUDIENCE: AreaAudience = {
  geometry: null,
  aorItemIds: [],
};

/**
 * The shape drawn on the map, if there is one.
 *
 * `GeoLocationComponent` always emits a single-feature collection, so taking
 * the first feature is not a truncation.
 */
export function geometryOf(location: GeoLocation | undefined): Geometry | null {
  return location?.geojson?.data?.features?.[0]?.geometry ?? null;
}

export function hasAreaSelection(audience: AreaAudience): boolean {
  return Boolean(audience.geometry) || audience.aorItemIds.length > 0;
}

/**
 * What to store for this send.
 *
 * A hand-picked list is stored as user ids only, so the area is not resolved
 * again later and cannot add people. Send to everyone stores the filter and
 * leaves the people to delivery. A home area is stored as area ids, never as
 * the area's shape.
 */
export function audienceForSubmit(values: {
  audience: AreaAudience;
  userIds: string[];
  teamIds: string[];
  sendToEveryone: boolean;
}): {
  userIds: string[];
  teamIds: string[];
  audienceGeometry: Geometry | null;
  audienceAorItemIds: string[];
  audienceLocationMatch: AudienceLocationMatch | null;
} {
  const homeArea =
    !values.audience.geometry && values.audience.aorItemIds.length > 0;
  const customLocation = Boolean(values.audience.geometry);

  if (values.sendToEveryone && homeArea) {
    return {
      userIds: [],
      teamIds: values.teamIds,
      audienceGeometry: null,
      audienceAorItemIds: values.audience.aorItemIds,
      audienceLocationMatch: 'home_aor',
    };
  }

  if (values.sendToEveryone && customLocation) {
    return {
      userIds: [],
      teamIds: values.teamIds,
      audienceGeometry: values.audience.geometry,
      audienceAorItemIds: [],
      audienceLocationMatch: 'current_location',
    };
  }

  return {
    userIds: values.userIds,
    teamIds: values.teamIds,
    audienceGeometry: null,
    audienceAorItemIds: [],
    audienceLocationMatch: null,
  };
}

/**
 * Why this send cannot go out yet, in the operator's terms.
 *
 * An area with nobody in it is the failure this whole mode invites: coverage
 * data is sparse, so a valid polygon over an unmapped region resolves to zero
 * recipients. That has to read as a refusal, not as a send.
 */
export function audienceError(values: {
  mode: AudienceMode;
  audience: AreaAudience;
  userIds: string[];
  previewCount: number | null;
  roleIds?: string[];
  roleScopes?: string[];
  teamIds?: string[];
  sendToEveryone?: boolean;
  filterActive?: boolean;
}): string | null {
  if (
    values.mode === AudienceMode.PEOPLE ||
    values.mode === AudienceMode.ROLE
  ) {
    const roleScopes = values.roleScopes ?? values.roleIds ?? [];
    const teamIds = values.teamIds ?? [];
    if (values.filterActive && values.previewCount === 0) {
      return 'Nobody in this area matches, so there is nobody to notify.';
    }
    if (values.filterActive && values.sendToEveryone) return null;
    if (values.userIds.length > 0 || teamIds.length > 0) return null;
    if (values.filterActive && roleScopes.length > 0) {
      return 'Nobody with the selected role is in this area, so there is nobody to notify.';
    }
    if (roleScopes.length > 0) {
      return 'Nobody has the selected role, so there is nobody to notify.';
    }
    if (values.mode === AudienceMode.ROLE) {
      return 'Select a role or a team';
    }
    return 'Please select at least one individual';
  }

  if (!hasAreaSelection(values.audience)) {
    return 'Draw an area or choose areas of responsibility to send to';
  }

  if (values.previewCount === 0) {
    return 'Nobody covers this area, so there is nobody to notify. Assign area coverage to members, or pick recipients individually.';
  }

  return null;
}

export function describeAudienceCount(count: number | null): string {
  if (count === null) return 'Counting recipients…';
  if (count === 0) return 'No recipients';

  return count === 1 ? '1 recipient' : `${count} recipients`;
}
